"use client";

import { createContext, useContext } from "react";
import type { UseRecoverableCountResult } from "../hooks/useRecoverableCount";
import type { RelayRecoveryProps } from "../types";

/**
 * What `<RelayRecovery>` publishes to its compound children.
 *
 * `props` is the root's own configuration (client/recipient/injected wallet
 * capabilities/callbacks), which `<RelayRecovery.Content>` needs but can't
 * receive as props (the host supplies it as a child).
 *
 * `recovery` is the root-level snapshot shared by `.Trigger` and `.Content`.
 * It lives here because the trigger needs the count while the modal is CLOSED,
 * and sharing its target data prevents the content from repeating the same
 * config/recovery fan-out on every open.
 *
 * Mirrors `RelayHistoryContext` (`./relay-history-provider.ts`) -- kept as its
 * own context rather than shared, since `<RelayDeposit>`, `<RelayHistory>` and
 * `<RelayRecovery>` are independent compounds that don't nest inside one another
 * (each owns its own `ResponsiveShell`/`RelayShellContext`).
 */
export interface RelayRecoveryContextValue {
  props: RelayRecoveryProps;
  recovery: UseRecoverableCountResult;
}

export const RelayRecoveryContext = createContext<RelayRecoveryContextValue | null>(null);

function useRelayRecoveryContextValue(): RelayRecoveryContextValue {
  const ctx = useContext(RelayRecoveryContext);
  if (!ctx) {
    throw new Error(
      "RelayRecovery compound components must be used within <RelayRecovery>",
    );
  }
  return ctx;
}

export function useRelayRecoveryContext(): RelayRecoveryProps {
  return useRelayRecoveryContextValue().props;
}

/** Root snapshot consumed by the recovery modal body. */
export function useRelayRecoverySnapshot(): UseRecoverableCountResult {
  return useRelayRecoveryContextValue().recovery;
}

/** `.Trigger`'s badge count. See `RelayRecoveryContextValue.recovery`. */
export function useRelayRecoveryCount(): number {
  return useRelayRecoveryContextValue().recovery.count;
}
